import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TextField,
} from '@material-ui/core';
import ShareIcon from '@material-ui/icons/Share';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import OpenInNewIcon from '@material-ui/icons/OpenInNew';
import DeleteIcon from '@material-ui/icons/Delete';
import { Divider } from '@material-ui/core';
import { toast } from 'react-toastify';
import api from '../../../../../services/api';

import BaseBox from '../_Common/BaseBox';
import NoData from '../_Common/NoData';
import { useStyles } from './ProfitAndLossStyles';
import CustomCircularProgress from '../_Common/CustomCircularProgress';
import AnimatedNumber from '../_Common/AnimatedNumber';
import { ApplicationState } from '../../../../../store';
import { formatPrice, setPriceColor } from '../../../../../utils/functions';

export default function ProfitAndLoss() {
  const classes = useStyles();
  // Raw numeric values for animation
  const [rawValues, setRawValues] = useState({
    today: { profit: 0, loss: 0, total: 0 },
    week: { profit: 0, loss: 0, total: 0 },
    month: { profit: 0, loss: 0, total: 0 },
    year: { profit: 0, loss: 0, total: 0 },
    allTime: { profit: 0, loss: 0, total: 0 },
  });
  const { loading, boxes } = useSelector(
    (state: ApplicationState) => state.boxes
  );

  useEffect(() => {
    if (boxes.ProfitAndLoss) {
      const pl = boxes.ProfitAndLoss;
      setRawValues({
        today: { profit: Number(pl.today.profit) || 0, loss: Number(pl.today.loss) || 0, total: Number(pl.today.total) || 0 },
        week: { profit: Number(pl.week.profit) || 0, loss: Number(pl.week.loss) || 0, total: Number(pl.week.total) || 0 },
        month: { profit: Number(pl.month.profit) || 0, loss: Number(pl.month.loss) || 0, total: Number(pl.month.total) || 0 },
        year: { profit: Number(pl.year.profit) || 0, loss: Number(pl.year.loss) || 0, total: Number(pl.year.total) || 0 },
        allTime: { profit: Number(pl.allTime.profit) || 0, loss: Number(pl.allTime.loss) || 0, total: Number(pl.allTime.total) || 0 },
      });
    }
  }, [boxes.ProfitAndLoss]);

  const periodKeys: Array<'today' | 'week' | 'month' | 'year' | 'allTime'> = ['today', 'week', 'month', 'year', 'allTime'];
  const periodLabelsArr = ['Hoje', 'Semana', 'Mes', 'Ano', 'Total'];

  const [shareOpen, setShareOpen] = useState(false);
  const [sharePeriod, setSharePeriod] = useState('month');
  const [shareLink, setShareLink] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [savedCards, setSavedCards] = useState<any[]>([]);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [cardsLoading, setCardsLoading] = useState(false);

  const periodLabels: Record<string, string> = {
    today: 'Hoje',
    week: 'Semana',
    month: 'Mês',
    year: 'Ano',
  };

  function loadCards() {
    setCardsLoading(true);
    api.get('/share').then(res => {
      setSavedCards(res.data);
      setCardsLoading(false);
    }).catch(() => {
      setCardsLoading(false);
    });
  }

  function handleShare() {
    setShareLoading(true);
    const offsetHours = new Date().getTimezoneOffset() / -60;
    api.post('/share', { period: sharePeriod, offsetHours }).then(res => {
      const link = `${window.location.origin}/share/${res.data.token}`;
      setShareLink(link);
      setShareLoading(false);
      loadCards();
    }).catch(() => {
      toast.error('Erro ao gerar card');
      setShareLoading(false);
    });
  }

  function handleCopyLink() {
    navigator.clipboard.writeText(shareLink).then(() => {
      toast.success('Link copiado!');
    });
  }

  function handleDeleteCard(token: string) {
    api.delete('/share/' + token).then(() => {
      setSavedCards(savedCards.filter(c => c.token !== token));
      toast.success('Card removido');
    }).catch(() => {
      toast.error('Erro ao remover card');
    });
  }

  function copyCardLink(token: string) {
    const link = window.location.origin + '/share/' + token;
    navigator.clipboard.writeText(link).then(() => {
      toast.success('Link copiado!');
    });
  }

  const hasData =
    rawValues.allTime.loss !== 0 ||
    rawValues.allTime.profit !== 0;

  const isFirstLoad = loading && !hasData;

  return (
    <BaseBox title="Lucro/Prejuizo" id="tour-box-profitloss"
      action={hasData && !isFirstLoad ? (
        <IconButton size="small" onClick={() => { setShareOpen(true); setShareLink(''); loadCards(); }}>
          <ShareIcon fontSize="small" />
        </IconButton>
      ) : undefined}
    >
      {!isFirstLoad && hasData && (
        <div className={classes.tableWrapper}>
          <div className={classes.headerRow}>
            <div className={classes.headerLabelFirst} />
            <Typography className={`${classes.headerLabel} ${classes.profitValue}`}>
              Lucro
            </Typography>
            <Typography className={`${classes.headerLabel} ${classes.lossValue}`}>
              Prejuizo
            </Typography>
            <div className={classes.totalDivider} />
            <Typography className={classes.headerLabel}>
              Saldo
            </Typography>
          </div>
          {periodKeys.map((key, i) => (
            <div
              key={key}
              className={`${classes.dataRow} ${i % 2 === 0 ? classes.dataRowHighlight : ''}`}
            >
              <Typography className={classes.periodLabel}>
                {periodLabelsArr[i]}
              </Typography>
              <Typography className={`${classes.valueCell} ${classes.profitValue}`}>
                <AnimatedNumber
                  value={rawValues[key].profit}
                  formatFn={formatPrice}
                />
              </Typography>
              <Typography className={`${classes.valueCell} ${classes.lossValue}`}>
                <AnimatedNumber
                  value={rawValues[key].loss}
                  formatFn={formatPrice}
                />
              </Typography>
              <div className={classes.totalDivider} />
              <Typography
                className={classes.valueCell}
                style={{ color: setPriceColor(rawValues[key].total), fontWeight: 600, transition: 'color 0.4s ease' }}
              >
                <AnimatedNumber
                  value={rawValues[key].total}
                  formatFn={formatPrice}
                />
              </Typography>
            </div>
          ))}
        </div>
      )}
      {isFirstLoad && <CustomCircularProgress />}
      {!isFirstLoad && !hasData && <NoData />}

      <Dialog open={shareOpen} onClose={() => setShareOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Compartilhar Resultado</DialogTitle>
        <DialogContent>
          <FormControl fullWidth style={{ marginBottom: '16px' }}>
            <InputLabel id="share-period-label">Período</InputLabel>
            <Select
              labelId="share-period-label"
              value={sharePeriod}
              onChange={(e: any) => { setSharePeriod(e.target.value); setShareLink(''); }}
            >
              <MenuItem value="today">Hoje</MenuItem>
              <MenuItem value="week">Semana</MenuItem>
              <MenuItem value="month">Mês</MenuItem>
              <MenuItem value="year">Ano</MenuItem>
            </Select>
          </FormControl>
          {shareLink && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={shareLink}
                InputProps={{ readOnly: true }}
              />
              <IconButton onClick={handleCopyLink} size="small">
                <FileCopyIcon fontSize="small" />
              </IconButton>
              <IconButton onClick={() => window.open(shareLink, '_blank')} size="small">
                <OpenInNewIcon fontSize="small" />
              </IconButton>
            </div>
          )}

          {savedCards.length > 0 && (
            <>
              <Divider style={{ margin: '20px 0 12px' }} />
              <Typography variant="subtitle2" style={{ marginBottom: '8px' }}>
                Cards criados
              </Typography>
              <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                {savedCards.map(card => {
                  const total = card.data && card.data.ProfitAndLoss ? card.data.ProfitAndLoss.total : null;
                  const dateRange = card.data && card.data.periodDateRange ? card.data.periodDateRange : '';
                  return (
                    <div key={card.token} style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      padding: '6px 0',
                      borderBottom: '1px solid rgba(255,255,255,0.1)',
                    }}>
                      <div>
                        <Typography variant="body2">
                          {periodLabels[card.period] || card.period}
                          {total !== null && (
                            <span style={{ color: total >= 0 ? '#4BDC4B' : '#FF6347', marginLeft: '8px' }}>
                              {total.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}
                            </span>
                          )}
                        </Typography>
                        <Typography variant="caption" style={{ color: '#ffffff99' }}>
                          {dateRange}
                        </Typography>
                      </div>
                      <div style={{ display: 'flex', gap: '2px' }}>
                        <IconButton size="small" onClick={() => copyCardLink(card.token)}>
                          <FileCopyIcon style={{ fontSize: '16px' }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => window.open(window.location.origin + '/share/' + card.token, '_blank')}>
                          <OpenInNewIcon style={{ fontSize: '16px' }} />
                        </IconButton>
                        <IconButton size="small" onClick={() => handleDeleteCard(card.token)}>
                          <DeleteIcon style={{ fontSize: '16px' }} />
                        </IconButton>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShareOpen(false)}>Fechar</Button>
          {!shareLink && (
            <Button onClick={handleShare} color="primary" variant="contained" disabled={shareLoading}>
              {shareLoading ? 'Gerando...' : 'Gerar Link'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </BaseBox>
  );
}
